// Un brouillon de relevé lu commande par commande, comme « Vos livraisons » du relevé envoyé à la Coop.
// Rien n'est recalculé : chaque ligne du brouillon est rattachée à sa commande, ou partagée au tonnage entre
// les commandes de son voyage (chargement du camion) ou de sa journée (inspection et retour), au centime près.
import { billingNumber } from "./billingCalculation.js";

const HOURLY_KINDS = new Set(["delivery", "loading", "preload", "inspection", "return"]);
const unique = (values) => [...new Set(values.filter(Boolean))];
const cents = (value) => Math.round((billingNumber(value) ?? 0) * 100);
const lineSeconds = (line) => line.billing_unit === "heure" ? (billingNumber(line.quantity) ?? 0) * 3600 : 0;

/** Partage des centimes au plus fort reste : la somme des parts redonne exactement le montant de la ligne. */
export function splitCents(total, weights) {
  const sum = weights.reduce((acc, weight) => acc + weight, 0);
  if (!weights.length) return [];
  const shares = weights.map((weight) => sum > 0 ? total * weight / sum : total / weights.length);
  const floors = shares.map(Math.floor);
  let rest = total - floors.reduce((acc, value) => acc + value, 0);
  shares.map((share, index) => [share - floors[index], index]).sort((a, b) => b[0] - a[0])
    .forEach(([, index]) => { if (rest > 0) { floors[index] += 1; rest -= 1; } });
  return floors;
}

// Route et temps sur place d'une ligne horaire, au prorata de son bloc enregistré (null : pas de partage connu).
function legSplit(line) {
  const block = line.calculation_details?.block || {};
  const travel = billingNumber(block.travel_seconds);
  const onsite = billingNumber(block.onsite_seconds);
  const seconds = lineSeconds(line);
  if (travel === null || onsite === null || travel + onsite <= 0) return { travel: null, onsite: seconds };
  return { travel: seconds * travel / (travel + onsite), onsite: seconds * onsite / (travel + onsite) };
}

function emptyRow(line) {
  return {
    key: line.presentation_context.order_id, orderId: line.presentation_context.order_id, reference: line.order_reference,
    date: line.service_date, start: null, truck: line.service_crew?.truck_number || null, driver: line.service_crew?.driver_name || null,
    farm: line.presentation_context.client_name || line.service_context?.customer_name || null, tons: billingNumber(line.presentation_context.tonnage),
    mills: [], origin: null, preloaded: false,
    toMill: { seconds: null, km: null }, loading: 0, toClient: { seconds: null, km: null }, onsite: 0, shared: 0, other: 0,
    seconds: 0, amountCents: 0, hourly: null, hourlyException: false, tonneRate: null, tonneException: false, fixedCents: null, fixedException: false,
    dompeur: false,
    segments: [], lines: [],
  };
}

const addSeconds = (target, value) => ({ ...target, seconds: value === null ? target.seconds : (target.seconds ?? 0) + value });

function addPart(row, line, kind, share, amountCents) {
  const seconds = lineSeconds(line) * share;
  const split = legSplit(line);
  row.seconds += seconds;
  row.amountCents += amountCents;
  row.lines.push(line);
  const sc = line.service_context || {};
  // Camion dompeur : la tonne remplace les heures.
  row.dompeur ||= kind === "dompeur";
  if (kind === "delivery") {
    row.toClient = addSeconds(row.toClient, split.travel === null ? null : split.travel * share);
    if (row.toClient.km === null && billingNumber(sc.route_km) !== null) row.toClient.km = billingNumber(sc.route_km);
    row.onsite += split.onsite * share;
    row.start = row.start || line.calculation_details?.block?.start || null;
    // Provenance de la marchandise seulement : l'origine du trajet peut être la ferme précédente.
    row.mills = unique([...row.mills, ...(sc.cargo_origins || []).map((origin) => origin.name)]);
    if (line.unit_rate != null) row.hourly = billingNumber(line.unit_rate);
    row.hourlyException ||= Boolean(line.calculation_details?.exceptional_rate);
  } else if (kind === "loading" || kind === "preload") {
    row.toMill = addSeconds(row.toMill, split.travel === null ? null : split.travel * share);
    if (row.toMill.km === null && billingNumber(sc.route_km) !== null) row.toMill.km = billingNumber(sc.route_km);
    row.loading += split.onsite * share;
    row.mills = unique([...row.mills, ...(sc.destination || []).map((place) => place.name)]);
    row.origin ||= sc.origin_kind || null;
    // Chargé la veille : seul le total du chargement est connu, la route vers la meunerie y est incluse.
    if (line.presentation_context?.order_id || kind === "preload") row.preloaded = true;
  } else if (kind === "inspection" || kind === "return") row.shared += seconds;
  else {
    row.other += seconds;
    if (kind === "fixed_fee") {
      row.fixedCents = (row.fixedCents ?? 0) + amountCents;
      row.fixedException ||= line.calculation_details?.fixed_fee_source !== "aspiration";
    } else if (line.billing_unit === "tonne" && line.unit_rate != null) {
      row.tonneRate = billingNumber(line.unit_rate);
      row.tonneException ||= Boolean(line.calculation_details?.exceptional_rate);
    }
  }
}

/** Les commandes du brouillon par journée de livraison, et les lignes qu'aucune commande ne porte. */
export function buildInvoiceOrderRows(lines = []) {
  const rows = new Map();
  const byReference = new Map();
  const sheetLines = lines.filter((line) => line && !line.is_manual && line.calculation_details?.revision_id);
  const own = sheetLines.filter((line) => line.presentation_context?.order_id);
  for (const line of own) {
    const id = line.presentation_context.order_id;
    if (!rows.has(id)) rows.set(id, emptyRow(line));
    const row = rows.get(id);
    // La livraison décrit la commande ; un Chargé PM rencontré avant elle ne porte que la meunerie.
    if (line.calculation_details.kind === "delivery") Object.assign(row, { date: line.service_date, truck: line.service_crew?.truck_number || row.truck,
      driver: line.service_crew?.driver_name || row.driver, farm: line.presentation_context.client_name || line.service_context?.customer_name || row.farm,
      tons: billingNumber(line.presentation_context.tonnage) ?? row.tons });
    byReference.set(line.order_reference, row);
  }
  // Commandes livrées par un relevé de journée : celles qui partagent son inspection et son retour.
  const byRevision = new Map();
  for (const line of own.filter((value) => value.calculation_details.kind === "delivery")) {
    const revision = line.calculation_details.revision_id;
    byRevision.set(revision, unique([...(byRevision.get(revision) || []), line.presentation_context.order_id]));
  }
  const other = [];
  for (const line of lines) {
    if (!line) continue;
    const kind = line.calculation_details?.kind;
    if (!sheetLines.includes(line)) { other.push(line); continue; }
    if (line.presentation_context?.order_id) {
      addPart(rows.get(line.presentation_context.order_id), line, kind, 1, cents(line.line_total));
      continue;
    }
    // Ligne du camion : le chargement va aux commandes de sa cargaison, l'inspection et le retour à celles du relevé.
    const cargo = unique((line.service_context?.cargo_origins || []).flatMap((origin) => origin.order_references || []))
      .map((reference) => byReference.get(reference)?.orderId);
    const targets = unique(kind === "loading" || kind === "preload" ? cargo : HOURLY_KINDS.has(kind) ? byRevision.get(line.calculation_details.revision_id) || [] : [])
      .map((id) => rows.get(id)).filter(Boolean);
    if (!targets.length) { other.push(line); continue; }
    const weights = targets.map((row) => row.tons ?? 1);
    const amounts = splitCents(cents(line.line_total), weights);
    const sum = weights.reduce((acc, weight) => acc + weight, 0);
    targets.forEach((row, index) => addPart(row, line, kind, sum > 0 ? weights[index] / sum : 1 / targets.length, amounts[index]));
  }
  const days = new Map();
  for (const row of rows.values()) {
    row.amount = row.amountCents / 100;
    row.fixed = row.fixedCents === null ? null : row.fixedCents / 100;
    row.hours = row.seconds / 3600;
    row.segments = [["road", row.toMill.seconds], ["loading", row.loading], ["road", row.toClient.seconds], ["delivery", row.onsite],
      ["shared", row.shared], ["other", row.other]].filter(([, seconds]) => seconds > 0).map(([kind, seconds]) => ({ kind, seconds }));
    if (!days.has(row.date)) days.set(row.date, { date: row.date, rows: [], amountCents: 0, seconds: 0 });
    const day = days.get(row.date);
    day.rows.push(row);
    day.amountCents += row.amountCents;
    day.seconds += row.seconds;
  }
  return {
    days: [...days.values()].sort((a, b) => String(a.date).localeCompare(String(b.date))).map((day) => ({
      ...day, amount: day.amountCents / 100, hours: day.seconds / 3600,
      rows: day.rows.sort((a, b) => String(a.start || "~").localeCompare(String(b.start || "~")) || String(a.reference).localeCompare(String(b.reference))),
    })),
    other,
  };
}
